import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Clock, Stethoscope, User } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import { PageHeader } from "@/components/layout/page-header";
import {
  MultiProviderCalendar,
  type ProviderColumn,
} from "@/components/calendar/multi-provider-calendar";
import type { CalendarEvent } from "@/components/calendar/calendar-event-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { localDateKey } from "@/lib/date/local-date-key";

interface CalendarSearch {
  /** Local calendar date YYYY-MM-DD; defaults to today. */
  date?: string;
}

interface FacilityCalendarPayload {
  providers: ProviderColumn[];
  events: (CalendarEvent & { providerId: string })[];
}

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/calendar")({
  validateSearch: (search: Record<string, unknown>): CalendarSearch => ({
    date: typeof search.date === "string" ? search.date : undefined,
  }),
  component: FacilityCalendarPage,
});

function FacilityCalendarPage() {
  const { facilityId } = Route.useParams();
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  // Local date keys — `toISOString()` is the UTC day, which in RO/BG (UTC+2/+3)
  // made "Next day" stay put, "Previous day" jump two days and "Today" point
  // at yesterday between 00:00 and 03:00.
  const todayStr = localDateKey();
  const activeDate = date ?? todayStr;

  const [data, setData] = useState<FacilityCalendarPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"no-access" | "load-failed" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<{
    event: CalendarEvent;
    provider: ProviderColumn;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiFetch
      .get<FacilityCalendarPayload>(
        `/api/account/facility/${facilityId}/calendar?date=${activeDate}`,
        { signal: controller.signal },
      )
      .then((d) => setData(d))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(facilityErrorKind(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [facilityId, activeDate, reloadKey]);

  const setDate = (next: string) => {
    void navigate({
      to: "/account/facility/$facilityId/calendar",
      params: { facilityId },
      search: { date: next },
    });
  };

  const shiftDay = (delta: number) => {
    const d = new Date(`${activeDate}T00:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(localDateKey(d));
  };

  const dateLabel = useMemo(() => {
    const d = new Date(`${activeDate}T00:00:00`);
    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(d);
  }, [activeDate, locale]);

  const currentDate = useMemo(() => new Date(`${activeDate}T00:00:00`), [activeDate]);

  if (loading) return <CalendarLoadingSkeleton />;

  if (error || !data) {
    return (
      <FacilityStateCard
        kind={error ?? "load-failed"}
        facilityId={facilityId}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={<Trans>Calendar</Trans>}
        description={<span className="capitalize">{dateLabel}</span>}
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => shiftDay(-1)}
              aria-label={t`Previous day`}
              className="size-11 p-0"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDate(todayStr)}
              disabled={activeDate === todayStr}
              className="min-h-11 px-4"
            >
              <Trans>Today</Trans>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => shiftDay(1)}
              aria-label={t`Next day`}
              className="size-11 p-0"
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
        }
      />

      {data.providers.length === 0 ? (
        <div className="border-border bg-card flex flex-col items-center gap-2 rounded-xl border py-12 text-center">
          <Stethoscope className="text-muted-foreground/40 size-8" aria-hidden />
          <p className="text-muted-foreground text-sm">
            <Trans>No providers assigned to this facility yet.</Trans>
          </p>
        </div>
      ) : (
        <MultiProviderCalendar
          providers={data.providers}
          events={data.events}
          currentDate={currentDate}
          onSelectEvent={(event, provider) => setSelected({ event, provider })}
        />
      )}

      <AppointmentSheet
        facilityId={facilityId}
        selected={selected}
        onClose={() => setSelected(null)}
        locale={locale}
      />

      <AccountSecurityBanner />
    </div>
  );
}

// Tap target for phones: the hover popover on the event card is desktop-only,
// so a tap opens these details instead. Only fields the calendar payload
// carries (no phone number — the endpoint doesn't send one).
function AppointmentSheet({
  facilityId,
  selected,
  onClose,
  locale,
}: {
  facilityId: string;
  selected: { event: CalendarEvent; provider: ProviderColumn } | null;
  onClose: () => void;
  locale: string;
}) {
  const { t } = useLingui();
  const event = selected?.event;
  const start = event ? new Date(event.startsAt) : null;
  const end = event ? new Date(event.endsAt) : null;
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
  const dayFmt = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <ResponsiveSheet
      open={selected !== null}
      onClose={onClose}
      title={<Trans>Appointment details</Trans>}
      closeLabel={t`Close`}
      footer={
        start ? (
          <Link
            to="/account/facility/$facilityId/appointments"
            params={{ facilityId }}
            // UTC day: the appointments page filters `?date=` against
            // `startsAt.toISOString()`, so a local key would miss a visit
            // booked between local midnight and 03:00.
            search={{ date: start.toISOString().slice(0, 10) }}
            className="border-border bg-card text-foreground hover:bg-muted/40 flex min-h-11 w-full items-center justify-center rounded-lg border px-4 text-sm font-medium transition-colors"
          >
            <Trans>Open in appointments</Trans>
          </Link>
        ) : null
      }
    >
      {event && start && end && selected ? (
        <dl className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <User className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <dt className="text-muted-foreground text-xs">
                <Trans>Patient</Trans>
              </dt>
              <dd className="text-foreground font-medium">{event.patientName ?? event.title}</dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <dt className="text-muted-foreground text-xs">
                <Trans>Time</Trans>
              </dt>
              <dd className="text-foreground">
                <span className="capitalize">{dayFmt.format(start)}</span>
                {" · "}
                <span className="tabular-nums">
                  {timeFmt.format(start)} – {timeFmt.format(end)}
                </span>
              </dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Stethoscope className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <dt className="text-muted-foreground text-xs">
                <Trans>Doctor</Trans>
              </dt>
              <dd className="text-foreground">{selected.provider.name}</dd>
            </div>
          </div>
          {event.reason || event.title ? (
            <div className="flex items-start gap-3">
              <span className="size-4 shrink-0" aria-hidden />
              <div className="min-w-0">
                <dt className="text-muted-foreground text-xs">
                  <Trans>Service</Trans>
                </dt>
                <dd className="text-foreground">{event.reason ?? event.title}</dd>
              </div>
            </div>
          ) : null}
          {event.status ? (
            <div className="flex items-start gap-3">
              <span className="size-4 shrink-0" aria-hidden />
              <div className="min-w-0">
                <dt className="text-muted-foreground text-xs">
                  <Trans>Status</Trans>
                </dt>
                <dd className="mt-0.5">
                  <Badge variant={event.status === "in_progress" ? "info" : "secondary"}>
                    {statusLabel(event.status)}
                  </Badge>
                </dd>
              </div>
            </div>
          ) : null}
        </dl>
      ) : null}
    </ResponsiveSheet>
  );
}

// The facility calendar endpoint returns only these three statuses.
function statusLabel(status: string): React.ReactNode {
  switch (status) {
    case "confirmed":
      return <Trans>Confirmed</Trans>;
    case "scheduled":
      return <Trans>Scheduled</Trans>;
    case "in_progress":
      return <Trans>In progress</Trans>;
    default:
      return status;
  }
}

function CalendarLoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-9 w-32" />
      </div>
      <Skeleton className="h-[600px] w-full rounded-xl" />
    </div>
  );
}
